<template>
  <div class="detail-root">
    <!-- Header -->
    <header class="detail-header">
      <ONEButton
        label="Back"
        icon="pi pi-arrow-left"
        class="p-button-text p-button-sm back-btn"
        @click="router.push('/news-events')"
      />
    </header>

    <!-- Skeleton loading -->
    <div v-if="loading" class="detail-skeleton">
      <ONESkeleton height="2rem" class="mb-3" />
      <ONESkeleton height="1rem" width="40%" class="mb-4" />
      <ONESkeleton height="300px" />
    </div>

    <!-- Not found -->
    <div v-else-if="!item" class="empty-state">
      <i class="pi pi-inbox text-4xl text-slate-300"></i>
      <p class="text-slate-500 mt-2">Item not found</p>
    </div>

    <!-- Detail content -->
    <div v-else class="detail-content">
      <!-- Cover image -->
      <div v-if="item.FullImagePath" class="cover-image-wrap">
        <img :src="item.FullImagePath" :alt="item.Title" class="cover-image" />
      </div>

      <!-- Meta row -->
      <div class="meta-row">
        <ONETag :value="item.EventType === 1 ? 'Event' : 'News'" severity="info" />
        <ONETag v-if="item.Category" :value="item.Category" severity="secondary" />
        <span class="meta-date">
          <i class="pi pi-calendar"></i>
          {{ formatDate(item.EventDate || item.CreatedDate) }}
        </span>
        <span v-if="item.CreatedByName" class="meta-author">
          <i class="pi pi-user"></i> {{ item.CreatedByName }}
        </span>
        <span v-if="readTime > 0" class="meta-readtime">
          <i class="pi pi-clock"></i> {{ readTime }} min read
        </span>
      </div>

      <!-- Title -->
      <h1 class="detail-title">{{ item.Title }}</h1>

      <!-- Rich HTML body -->
      <div v-if="item.ContentHtml" class="html-body ql-editor" v-html="item.ContentHtml"></div>

      <!-- Plain description fallback -->
      <p v-else-if="item.Description" class="plain-body">{{ item.Description }}</p>

      <!-- Attachment -->
      <div v-if="item.FullAttachmentPath || item.AttachmentPath" class="attachment-section">
        <a
          :href="item.FullAttachmentPath || item.AttachmentPath"
          target="_blank"
          class="attachment-link"
        >
          <i class="pi pi-paperclip"></i> Download Attachment
        </a>
      </div>

      <!-- Admin actions -->
      <div v-if="isAdmin" class="admin-actions">
        <ONEButton
          label="Edit"
          icon="pi pi-pencil"
          class="p-button-outlined p-button-sm"
          @click="router.push(`/news-events/${item.Id}/edit`)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import moment from 'moment'
import { getNewsEventById, getDashboardUserContext } from '../../services/dashboardService'

const router = useRouter()
const route = useRoute()

const loading = ref(true)
const item = ref<any>(null)

const ctx = getDashboardUserContext()
const isAdmin = ctx.isAdmin

const readTime = computed(() => {
  const text = (item.value?.ContentHtml?.replace(/<[^>]+>/g, '') ?? '') +
               (item.value?.Description ?? '')
  const words = text.trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.ceil(words / 200))
})

function formatDate(d: any) {
  if (!d) return ''
  const m = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
  return m.isValid() ? m.format('DD MMM YYYY') : moment(d).format('DD MMM YYYY')
}

onMounted(async () => {
  const id = Number(route.params.id)
  if (id) item.value = await getNewsEventById(id)
  loading.value = false
})
</script>

<style scoped>
.detail-root {
  min-height: 100vh;
  background: var(--one-rule-soft);
  padding: 1.5rem;
  font-family: 'Inter', sans-serif;
}
.detail-header { margin-bottom: 1rem; }
.back-btn { color: var(--one-muted); }

.detail-skeleton { max-width: 860px; margin: 0 auto; }

.empty-state {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 4rem; text-align: center;
}

.detail-content {
  max-width: 860px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid var(--one-rule);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}

.cover-image-wrap {
  margin: -2rem -2rem 1.5rem -2rem;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  max-height: 320px;
}
.cover-image { width: 100%; height: 100%; object-fit: cover; }

.meta-row {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 0.75rem; margin-bottom: 1rem;
  font-size: 0.82rem; color: var(--one-muted);
}
.meta-date, .meta-author, .meta-readtime {
  display: inline-flex; align-items: center; gap: 4px;
}

.detail-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 1.25rem 0;
  line-height: 1.3;
}

/* Quill HTML content */
.html-body {
  font-size: 0.95rem;
  line-height: 1.75;
  color: #334155;
  padding: 0;
}
.plain-body {
  font-size: 0.95rem;
  line-height: 1.75;
  color: #334155;
  white-space: pre-wrap;
}

.attachment-section {
  border-top: 1px solid var(--one-rule-soft);
  padding-top: 1rem;
  margin-top: 1.5rem;
}
.attachment-link {
  display: inline-flex; align-items: center; gap: 6px;
  color: #0d9488; text-decoration: none;
  padding: 6px 14px; border: 1px solid #5eead4;
  border-radius: 8px; background: #f0fdfa;
  font-size: 0.85rem; transition: all 0.15s;
}
.attachment-link:hover { background: #ccfbf1; }

.admin-actions {
  border-top: 1px solid var(--one-rule-soft);
  padding-top: 1rem; margin-top: 1rem;
  display: flex; gap: 0.5rem;
}
</style>
